// 介錯「執行の咆哮」(executioner_phaseTransition) の床の波を弾ける比較版 (2026-10-09・開発版だけ)。
//
//   この比較版が前提にする床の帯の版 (DEV ?roarAir=0): 波3つ (1200/1500/1800ms・各250ms) は弾けない床。答えは立ち位置 (ボスの真上 ±44px か、外へ 256px 以上)。
//   配布版の咆哮は 10-12 から空気の衝撃波 (src/exec_roar_air.ts) なので、この比較版は ?roarAir=0 と一緒の時だけ意味がある。
//   ユーザー (10-09): 「1つでも弾けば対局側（スペースが多い方）にふっとばされるようにしてほしい。
//     方向後の攻撃が距離を取っている方が見てから押す意識でパリィが間に合うようになっているので」
//   読み = 波をパリィで受けられるようにし、1つでも弾いたら主人公を場の広い方へ大きく吹き飛ばす。
//     咆哮の後の突進斬り (2300ms・白) は、距離があるほど見てから弾ける作りだから。
//   文書 = docs/research/2026-10-09-executioner-roar-parry-knockback-status.md
//
//   ?roarParry=1 でだけ変える。指定なし (now) = 今のまま。
//     &roarKnock=<px>         吹き飛ぶ距離。既定 300。比べる段は 200 / 300 / 400 (0〜800 の数なら何でも受ける)。
//     &roarEscape=keep|none   弾かずに立ち位置で避ける道。keep (既定) = 今の帯のまま (真上・外 256px 以上は当たらない)。
//                             none = 波1を真上まで、波3を壁まで広げる (止まって避けられる所が無い・弾くか受けるか)。
//     &roarKnockDir=roomy|away 吹き飛ぶ向き。roomy (既定) = 主人公の位置から左右の壁までの余白が大きい方 (窓口の読み)。
//                             away = ボスから離れる向き (波の輪が外へ押す向き)。読みが割れた時に比べる用。
//   1つ弾いたら、この回の残りの波はもう当たらない (描画は残る)。理由は文書の「残りの波」。
//   弾いた時の傷・体幹は普段のパリィと同じ (傷0・主人公の体幹は通常/ジャストの量・ボスの体幹が溜まる)。
//   配布版は game.ts の import.meta.env.DEV の中からしか呼ばれないので、この比較版は配布ビルドに入らない。
import { getAttack } from './attacks'
import type { HazardSpawn } from './types'

export type RoarWaveMode = 'now' | 'parry'
export type RoarEscape = 'keep' | 'none'
export type RoarKnockDir = 'roomy' | 'away'

export const ROAR_ATTACK_ID = 'executioner_phaseTransition'
export const ROAR_KNOCK_DEFAULT_PX = 300
export const ROAR_KNOCK_LEVELS_PX = [200, 300, 400] as const
const ROAR_KNOCK_MAX_PX = 800
// none: 波1の帯を真上まで (中心から 0〜120)、波3の帯を壁まで (180〜1000・壁の外は spawnZoneHits が切る)。
//   輪の絵の半径は今と同じ (90 / 210) に固定する (帯の中心で描くと輪が小さく・大きくなりすぎるため)。
const NONE_WAVE1 = { offset: 60, width: 120, ringRadiusPx: 90 }
const NONE_WAVE3 = { offset: 590, width: 820, ringRadiusPx: 210 }

export type RoarWaveConfig = { mode: RoarWaveMode; knockPx: number; escape: RoarEscape; dir: RoarKnockDir }
const NOW: RoarWaveConfig = { mode: 'now', knockPx: ROAR_KNOCK_DEFAULT_PX, escape: 'keep', dir: 'roomy' }

let config: RoarWaveConfig = NOW
let saved: HazardSpawn[] | undefined | null = null

export function roarWaveConfig(): RoarWaveConfig { return config }

export function parseRoarWave(
  rawWave: string | null, rawKnock: string | null = null, rawEscape: string | null = null, rawDir: string | null = null,
): RoarWaveConfig {
  if (rawWave !== '1' && rawWave !== 'parry') return NOW
  const n = rawKnock === null ? NaN : Number(rawKnock)
  const knockPx = Number.isFinite(n) && n >= 0 && n <= ROAR_KNOCK_MAX_PX ? n : ROAR_KNOCK_DEFAULT_PX
  return {
    mode: 'parry',
    knockPx,
    escape: rawEscape === 'none' ? 'none' : 'keep',
    dir: rawDir === 'away' ? 'away' : 'roomy',
  }
}

// URL の値から切り替える。roarParry=1 以外 (指定なし含む) は何もしない = 今のまま。
export function setRoarWaveFromParams(
  rawWave: string | null, rawKnock: string | null, rawEscape: string | null, rawDir: string | null,
): void {
  const c = parseRoarWave(rawWave, rawKnock, rawEscape, rawDir)
  if (c.mode !== 'now') setRoarWaveConfig(c)
}

export function setRoarWaveConfig(next: RoarWaveConfig): void {
  const atk = getAttack(ROAR_ATTACK_ID)
  // いったん今の配布版へ戻してから当てる (版どうしを重ねない)。
  if (saved !== null) {
    atk.hazardSpawns = saved
    saved = null
  }
  if (next.mode === 'parry') {
    saved = atk.hazardSpawns
    atk.hazardSpawns = (atk.hazardSpawns ?? []).map((s) => {
      const side = Math.sign(s.offset) || 1
      const wave = Math.abs(s.offset)
      const shape = next.escape === 'none' && wave === 90 ? NONE_WAVE1
        : next.escape === 'none' && wave === 210 ? NONE_WAVE3
        : undefined
      return {
        ...s,
        ...(shape ? { offset: side * shape.offset, width: shape.width, ringRadiusPx: shape.ringRadiusPx } : null),
        parryable: true,
        parryKnockbackPx: next.knockPx,
        parryKnockbackToward: next.dir,
        parryEndsCast: true,
      }
    })
  }
  config = next.mode === 'parry' ? next : NOW
}

export function resetRoarWave(): void { setRoarWaveConfig(NOW) }
// 吹き飛ぶ向きの計算は hits.ts の parryKnockSide (配布版の hits.ts からこのファイルを読まないため)。
